import { useCallback, useEffect, useState } from 'react';
import { CirclePause, MonitorCheck, MonitorOff, Printer, RefreshCw, Store, Undo2 } from 'lucide-react';
import { describeError, getPrintingFleet, type PrintingFleetResponse } from '@/lib/api';
import { share } from '@/lib/printing';
import { StatCard } from '@/components/ui';
import { CardTable, THead, TBody, Tr, Th, Td } from '@/components/CardTable';
import { AgentReleasesSection } from '@/components/AgentReleasesSection';

/**
 * Impressão nos pontos de produção: a frota (change production-printing, §15.2).
 *
 * A pergunta da tela é "a impressão está saindo?", e ela se responde por
 * contagens: lojas ativas e pausadas, computadores online e offline, versões em
 * campo e o que aconteceu com os cupons nas últimas 24 horas. A resposta da API
 * traz **só contagens e versões** — nenhum id de loja, pedido ou computador.
 * Para investigar uma loja, o operador abre o detalhe do tenant, seção
 * "Impressão".
 *
 * Nada aqui altera a impressão: pausar, retomar, desativar e reativar são da
 * própria loja, na aba Impressão do painel. O que a plataforma controla são as
 * duas flags, explicadas no fim da página, e — na seção "Versões do agente"
 * (change print-agent), só para `Admin` — a mínima e a recomendada de cada canal.
 */

const intFmt = new Intl.NumberFormat('pt-BR');
const timeFmt = new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' });

export function PrintingFleetPage() {
  const [data, setData] = useState<PrintingFleetResponse | null>(null);
  const [loadedAt, setLoadedAt] = useState<Date | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let cancelled = false;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoading(true);
    setError(null);
    getPrintingFleet()
      .then((res) => {
        if (cancelled) return;
        setData(res);
        setLoadedAt(new Date());
      })
      .catch((err) => !cancelled && setError(describeError(err)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [reload]);

  const refresh = useCallback(() => setReload((n) => n + 1), []);

  return (
    <div className="page">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0 }}>Impressão</h1>
        <span style={{ flex: 1 }} />
        {loadedAt && <span className="muted">Atualizado às {timeFmt.format(loadedAt)}</span>}
        <button type="button" className="btn btn-secondary btn-sm" onClick={refresh} disabled={loading}>
          <RefreshCw size={15} /> Atualizar
        </button>
      </div>

      {error && (
        <div className="error-banner" role="alert">
          {error}
        </div>
      )}
      {loading && !data && <p className="muted">Carregando…</p>}

      {data && (
        <>
          <StoresAndAgents fleet={data} />
          <JobsCard jobs={data.jobsLast24h} />
          <IncidentsCard incidents={data.incidentsLast24h} />
          <VersionsCard fleet={data} />
        </>
      )}

      {/* Separada das contagens da frota: carrega e falha sozinha. */}
      <AgentReleasesSection reloadKey={reload} />

      <FlagsNote />
    </div>
  );
}

function StoresAndAgents({ fleet }: { fleet: PrintingFleetResponse }) {
  return (
    <section aria-label="Lojas e computadores">
      <div className="stat-grid">
        <StatCard
          label="Lojas com impressão ativa"
          value={intFmt.format(fleet.stores.active)}
          delta="pausadas ou não"
          icon={<Store size={22} />}
        />
        <StatCard
          label="Lojas pausadas"
          value={intFmt.format(fleet.stores.paused)}
          delta="entre as ativas: pedidos sem papel"
          deltaTone={fleet.stores.paused > 0 ? 'error' : 'neutral'}
          icon={<CirclePause size={22} />}
        />
        <StatCard
          label="Desativaram em 30 dias"
          value={intFmt.format(fleet.stores.deactivatedLast30Days)}
          delta="pela aba Impressão"
          icon={<Undo2 size={22} />}
        />
        <StatCard
          label="Computadores online"
          value={intFmt.format(fleet.agents.online)}
          delta="contato nos últimos 90 s"
          deltaTone={fleet.agents.online > 0 ? 'success' : 'neutral'}
          icon={<MonitorCheck size={22} />}
        />
        <StatCard
          label="Computadores offline"
          value={intFmt.format(fleet.agents.offline)}
          delta="pareados, sem contato"
          deltaTone={fleet.agents.offline > 0 ? 'error' : 'neutral'}
          icon={<MonitorOff size={22} />}
        />
      </div>
    </section>
  );
}

function JobsCard({ jobs }: { jobs: PrintingFleetResponse['jobsLast24h'] }) {
  const rows: Array<{ label: string; count: number; hint: string }> = [
    { label: 'Enviados à impressora', count: jobs.printed, hint: 'confirmados pelo computador' },
    { label: 'Falharam', count: jobs.failed, hint: 'impressora inacessível, sem impressora ou cupom não gerado' },
    { label: 'Vencidos', count: jobs.expired, hint: 'não saíram até a virada das 03:00' },
    { label: 'Cancelados', count: jobs.cancelled, hint: 'pedido cancelado, pausa, desativação, corte ou desligamento' },
    { label: 'Na fila', count: jobs.pending, hint: 'aguardando o computador buscar' },
    { label: 'Entregues ao computador', count: jobs.delivered, hint: 'aguardando a confirmação' },
  ];

  return (
    <section className="card card-flush">
      <div className="card-title" style={{ padding: '16px 16px 0' }}>
        Cupons nas últimas 24 horas
      </div>
      <p className="muted" style={{ padding: '0 16px', margin: '6px 0 0' }}>
        {intFmt.format(jobs.total)} cupons criados · {share(jobs.printed, jobs.total)} enviados à impressora ·{' '}
        {share(jobs.failed, jobs.total)} falharam · {share(jobs.expired, jobs.total)} vencidos
      </p>
      <CardTable>
        <THead>
          <Tr>
            <Th>Estado</Th>
            <Th>Cupons</Th>
            <Th>Proporção</Th>
            <Th>O que significa</Th>
          </Tr>
        </THead>
        <TBody>
          {rows.map((row) => (
            <Tr key={row.label}>
              <Td heading>{row.label}</Td>
              <Td label="Cupons">{intFmt.format(row.count)}</Td>
              <Td label="Proporção">{share(row.count, jobs.total)}</Td>
              <Td label="O que significa" className="muted">
                {row.hint}
              </Td>
            </Tr>
          ))}
        </TBody>
      </CardTable>
    </section>
  );
}

function IncidentsCard({ incidents }: { incidents: PrintingFleetResponse['incidentsLast24h'] }) {
  return (
    <section className="card">
      <div className="card-title" style={{ marginBottom: 12 }}>
        Incidentes nas últimas 24 horas
      </div>
      <div className="detail-grid">
        <div className="k">Atrasos</div>
        <div>{intFmt.format(incidents.overdue)} cupons sem confirmação em 60 s</div>
        <div className="k">Falhas</div>
        <div>{intFmt.format(incidents.failed)} cupons</div>
        <div className="k">Falhas de renderização</div>
        <div>{intFmt.format(incidents.renderFailed)} cupons que o servidor não conseguiu gerar</div>
        <div className="k">Despachos isolados</div>
        <div>{intFmt.format(incidents.dispatchIsolated)} eventos de pedido isolados depois de 5 falhas</div>
      </div>

      {incidents.renderFailed > 0 && (
        <div className="warning-banner" role="status" style={{ marginTop: 12 }}>
          <strong>Cupom que não renderiza é defeito de layout.</strong> O pedido aparece como "Falhou"
          para a loja, os outros cupons continuam saindo, e o conserto é um deploy da API. Procure o
          log <code>printing.job_finished</code> com <code>render_failed</code>.
        </div>
      )}
      {incidents.dispatchIsolated > 0 && (
        <div className="warning-banner" role="status" style={{ marginTop: 12 }}>
          <strong>Evento de pedido isolado.</strong> Os cupons daquele aceite não foram criados: o
          pedido mostra "Falhou" e a loja pode reimprimir. Procure o log{' '}
          <code>printing.dispatch_isolated</code>, que traz o erro.
        </div>
      )}
    </section>
  );
}

function VersionsCard({ fleet }: { fleet: PrintingFleetResponse }) {
  return (
    <section className="card">
      <div className="card-title" style={{ marginBottom: 12 }}>
        Versões em campo
      </div>
      {fleet.agentVersions.length === 0 ? (
        <p className="muted" style={{ margin: 0 }}>
          Nenhum computador pareado.
        </p>
      ) : (
        <div className="grid-halves">
          <div>
            <div className="muted" style={{ marginBottom: 6 }}>
              Agente
            </div>
            <ul aria-label="Versões do agente" style={{ margin: 0, paddingLeft: 18 }}>
              {fleet.agentVersions.map((v) => (
                <li key={v.version ?? 'desconhecida'}>
                  <span className="mono">{v.version ?? 'desconhecida'}</span> —{' '}
                  {v.agents === 1 ? '1 computador' : `${intFmt.format(v.agents)} computadores`}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="muted" style={{ marginBottom: 6 }}>
              Protocolo
            </div>
            <ul aria-label="Versões do protocolo" style={{ margin: 0, paddingLeft: 18 }}>
              {fleet.protocolVersions.map((v) => (
                <li key={v.version ?? 'desconhecida'}>
                  <span className="mono">{v.version ?? 'desconhecido'}</span> —{' '}
                  {v.agents === 1 ? '1 computador' : `${intFmt.format(v.agents)} computadores`}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </section>
  );
}

function FlagsNote() {
  return (
    <section className="card">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <Printer size={18} />
        <div className="card-title">Piloto e desligamento geral</div>
      </div>
      <p>
        Uma loja entra no piloto com o override ligado de <strong>panel.printing</strong> para o
        tenant, em <em>Feature flags</em>. Remover o override tira a loja do piloto: para ela, a
        impressão deixa de existir.
      </p>
      <p>
        Para desligar a impressão em toda a plataforma, desligue a flag global{' '}
        <strong>printing-enabled</strong>. Os computadores são suspensos, a fila de impressão é
        cancelada e não volta sozinha, a aba Impressão das lojas fica em modo leitura com o aviso
        "Impressão desativada temporariamente pela Vapt", e "Imprimir pelo navegador" continua. O
        aceite automático das lojas com a impressão ativa, pausadas ou não, volta ao manual:{' '}
        <strong>avise as lojas do piloto antes</strong>.
      </p>
      <p className="muted" style={{ marginBottom: 0 }}>
        O backoffice não pausa, retoma, desativa nem reativa a impressão de uma loja. Quem faz isso é
        a própria loja, na aba Impressão do painel; o suporte orienta o dono.
      </p>
    </section>
  );
}

export default PrintingFleetPage;
